import { useReactiveVar } from '@apollo/client';
import { useCallback } from 'react';
import { useLocalStorage } from 'react-use';

import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { featureFlagsVar } from '@/graphql/reactiveVars';
import { localStorageStringArraySerializer } from '@/utils/localStorageSerializers';

import {
  FeatureAnnouncementConfig,
  FeatureAnnouncementId,
} from '../featureAnnouncements.config';
import {
  getUnviewedAnnouncements,
  markAnnouncementAsViewed,
  markAnnouncementsAsViewed,
} from '../featureAnnouncements.utils';

interface UseFeatureAnnouncementsReturn {
  /** List of unviewed announcements that should be shown */
  unviewedAnnouncements: FeatureAnnouncementConfig[];
  /** Count of unviewed announcements for badge display */
  unviewedCount: number;
  /** Function to mark an announcement as viewed */
  markAsViewed: (announcementId: FeatureAnnouncementId) => void;
  /** Function to mark multiple announcements as viewed */
  markMultipleAsViewed: (announcementIds: FeatureAnnouncementId[]) => void;
  /** All viewed announcement IDs */
  viewedIds: string[];
}

/**
 * Hook for managing feature announcements state
 * - Filters announcements by date, feature flags, and viewed status
 * - Provides functions to mark announcements as viewed
 * - Persists viewed state in localStorage
 */
export function useFeatureAnnouncements(): UseFeatureAnnouncementsReturn {
  // Recheck eligibility when the server's flag evaluations arrive or change.
  useReactiveVar(featureFlagsVar);
  const [viewedIds, setViewedIds] = useLocalStorage<string[]>(
    LOCAL_STORAGE_KEYS.FEATURE_ANNOUNCEMENTS_VIEWED,
    [],
    localStorageStringArraySerializer
  );

  const unviewedAnnouncements = getUnviewedAnnouncements(viewedIds ?? []);
  const unviewedCount = unviewedAnnouncements.length;

  const markAsViewed = useCallback(
    (announcementId: FeatureAnnouncementId) => {
      const updatedViewedIds = markAnnouncementAsViewed(
        announcementId,
        viewedIds ?? []
      );
      setViewedIds(updatedViewedIds);
    },
    [setViewedIds, viewedIds]
  );

  const markMultipleAsViewed = useCallback(
    (announcementIds: FeatureAnnouncementId[]) => {
      const updatedViewedIds = markAnnouncementsAsViewed(
        announcementIds,
        viewedIds ?? []
      );
      setViewedIds(updatedViewedIds);
    },
    [setViewedIds, viewedIds]
  );

  return {
    unviewedAnnouncements,
    unviewedCount,
    markAsViewed,
    markMultipleAsViewed,
    viewedIds: viewedIds ?? [],
  };
}
